import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Manager } from 'socket.io-client'
import { useUserContext } from '../../context/users/UserContext'
import { Card, Modal, Button, Row, Col, Badge } from 'react-bootstrap'
import { getByIdPA, patchByIdPA, addPA } from '../../context/users/UserActions'
import { deleteByIdPA } from '../../context/users/UserActions'
import { addQuestionToSession, createQuestion } from '../../context/questions/QuestionActions'
import { 
  FaUsers, 
  FaQuestionCircle, 
  FaChartLine, 
  FaClock,
  FaPlus,
  FaTimes,
  FaExclamationTriangle,
  FaEye,
  FaTrash,
  FaCopy,
  FaSignOutAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaInbox,
  FaExpandAlt,
  FaUser,
  FaSearch,
  FaTimesCircle,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaWifi,
  FaExclamationCircle,
  FaPhone,
  FaArrowLeft,
  FaHome,
  FaImages
} from 'react-icons/fa'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import AddQuestionToCanvas from './AddQuestionToCanvas'
import AddPhotoToCanvas from './AddPhototoCanvas'
import VideoTutorial from './VideoTutorial'
import './HostCanvas.css'

const listenerServiceUrl = process.env.REACT_APP_LISTENER_SERVICE || window.location.origin
const manager = new Manager(listenerServiceUrl)
const socket = manager.socket('/')

export default function HostCanvas() {
  const navigate = useNavigate()
  const { session, setSession, authenticatedUser, setAlert } = useUserContext()
  const [validSession, setValidSession] = useState(false)
  const [message, setMessage] = useState('')
  const [sessionOpen, setSessionOpen] = useState(true)
  const [sessionStartTime, setSessionStartTime] = useState(null)
  const [sessionDuration, setSessionDuration] = useState('00:00')
  
  // Estados para manejo de carga
  const [isLoading, setIsLoading] = useState(true)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Conectando con la sesión...')

  // Estados para métricas
  const [connectedGuests, setConnectedGuests] = useState(0)
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [totalResponses, setTotalResponses] = useState(0)
  const [completionRate, setCompletionRate] = useState(0)

  // Estados para modales
  const [show, setShow] = useState(false)
  const [showPhoto, setShowPhoto] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showQuestionDetail, setShowQuestionDetail] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [showImageViewer, setShowImageViewer] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  
  // Estados para vistas
  const [showUserDetail, setShowUserDetail] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  
  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredUsers, setFilteredUsers] = useState([])

  // Estados para preguntas
  const [newQuestion, setNewQuestion] = useState(false)
  const [question, setQuestion] = useState('')
  const [selectQuestion, setSelectQuestion] = useState(0)
  const [photoObjs, setPhotosObjs] = useState([])
  const [questionToDelete, setQuestionToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)

  const params = useParams()

  // Calcular métricas
  useEffect(() => {
    if (session && session.sessionUsers && session.questions) {
      const guests = session.sessionUsers.filter(user => user.userRole === 'INVITADO')
      setConnectedGuests(guests.length)
      setTotalQuestions(session.questions.length)
      
      let totalPossibleResponses = guests.length * session.questions.length
      let actualResponses = 0
      
      guests.forEach(user => {
        if (user.userAnswers) {
          user.userAnswers.forEach(answer => {
            if (answer.responses && answer.responses.length > 0) {
              actualResponses++
            }
          })
        }
      })
      
      setTotalResponses(actualResponses)
      setCompletionRate(totalPossibleResponses > 0 ? Math.round((actualResponses / totalPossibleResponses) * 100) : 0)
    }
  }, [session])

  // Filtrar usuarios por término de búsqueda
  useEffect(() => {
    if (session && session.sessionUsers) {
      const guests = session.sessionUsers.filter(user => user && user.userRole === 'INVITADO')
      
      if (searchTerm.trim() === '') {
        setFilteredUsers(guests)
      } else {
        const filtered = guests.filter(user => {
          if (!user) return false
          
          const fullName = user.userFullName || ''
          const email = user.userEmail || ''
          const searchLower = searchTerm.toLowerCase()
          
          return fullName.toLowerCase().includes(searchLower) ||
                 email.toLowerCase().includes(searchLower)
        })
        setFilteredUsers(filtered)
      }
    }
  }, [session, searchTerm])

  // Timer de duración de sesión
  useEffect(() => {
    if (sessionStartTime) {
      const interval = setInterval(() => {
        const now = new Date()
        const diff = now - sessionStartTime
        const minutes = Math.floor(diff / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        setSessionDuration(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [sessionStartTime])

  // Polling para actualizar datos de sesión cada 4 segundos
  useEffect(() => {
    if (validSession && session?.sessionId) {
      const fetchSessionData = async () => {
        try {
          const sessionData = await getByIdPA({
            path: 'canvas',
            id: params.id,
            token: authenticatedUser.value || authenticatedUser.token
          })
          
          if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionId')) {
            // Filtrar solo usuarios invitados para el anfitrión y ordenar alfabéticamente
            const invitedUsers = (sessionData?.sessionUsers || []).filter((user) => user.userRole === 'INVITADO')
            sessionData.sessionUsers = sortUsersByName(invitedUsers)
            setSession(sessionData)
          }
        } catch (error) {
          console.log('Error al actualizar datos de sesión:', error)
        }
      }

      // Ejecutar inmediatamente
      fetchSessionData()
      
      // Configurar intervalo de 4 segundos
      const interval = setInterval(fetchSessionData, 4000)

      return () => clearInterval(interval)
    }
  }, [validSession, session?.sessionId, params.id, authenticatedUser.value, authenticatedUser.token])

  // Función helper para ordenar usuarios alfabéticamente
  const sortUsersByName = (users) => {
    return users.sort((a, b) => {
      const nameA = (a.userFullName || '').toLowerCase()
      const nameB = (b.userFullName || '').toLowerCase()
      return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' })
    })
  }

  const fetchData = async () => {
    try {
      setIsLoading(true)
      setLoadingTimeout(false)
      setLoadingMessage('Conectando con la sesión...')
      
      // Timeout de 60 segundos
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          setLoadingTimeout(true)
          setLoadingMessage('Error de conexión')
        }
      }, 60000)
      
      const sessionData = await getByIdPA({ 
        path: 'canvas', 
        id: params.id, 
        token: authenticatedUser.value || authenticatedUser.token 
      })
      
      clearTimeout(timeoutId)
      
      if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionId')) {
        setLoadingMessage('Sesión cargada exitosamente')
        
        socket.emit('Canvas', { 
          session: params.id, 
          type: 'START_SESSION', 
          user: sessionData.user, 
          userId: authenticatedUser.userId 
        })
        
        // Filtrar y ordenar alfabéticamente los usuarios invitados
        const invitedUsers = (sessionData?.sessionUsers || []).filter((user) => user.userRole === 'INVITADO')
        sessionData.sessionUsers = sortUsersByName(invitedUsers)
        setSession(sessionData)
        setValidSession(true)
        setSessionStartTime(new Date())
        
        const queryParams = new URLSearchParams(window.location.search)
        setShowVideo(queryParams.get('newSession') === '1')
        
        localStorage.setItem('alleanza_currentSession', JSON.stringify(sessionData))
        
        // Pequeño delay para mostrar el mensaje de éxito
        setTimeout(() => {
          setIsLoading(false)
        }, 500)
      } else {
        setMessage(sessionData.message || 'Error al cargar la sesión')
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching session data:', error)
      setLoadingTimeout(true)
      setLoadingMessage('Error de conexión')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Funciones de preguntas (copiadas de Canvas.js)
  const handleChangeAddQuestions = async () => {
    setShow(true)
  }

  const handleClose = () => setShow(false)

  const onClickSelectPics = async () => {
    setShow(false)
    setShowPhoto(true)

    if (question.length > 3 && newQuestion) {
      let response = await createQuestion({
        path: 'questions',
        data: {
          question: question,
          "type": "NEW"
        },
        token: authenticatedUser.value
      })

      if (response.status === 200 && response.data) {
        setSelectQuestion(response.data.questionId)
      }
      if (response.status !== 200) {
        setAlert({
          message: response.message,
          msgType: 'warning'
        })
      }
    }
  }

  const handleOnSelectQuestion = (e) => {
    const value = e.target.value
    setSelectQuestion(value)
  }

  const handleChangeQuestion = async (e) => {
    const value = e.target.value;
    if (value.length > 3 && show && newQuestion) {
      setQuestion(value)
    }
  }

  const handleAddNewQuestionToggle = () => {
    setNewQuestion(!newQuestion)
    setQuestion('');
  }

  const updateUserQuestions = ({ userId, sessionId, sessionCode, currentQuestions, newQuestion }) => {
    const result = currentQuestions
    const questionExists = currentQuestions.some((question) => question.sessionQstId === newQuestion.sessionQstId)
    if (!questionExists) {
      result.push({
        sessionId,
        sessionCode,
        userId,
        ...newQuestion,
      })
    }
    return result
  }

  const saveQuestionToSession = async () => {
    if (photoObjs.length > 0 && selectQuestion !== 0) {
      const data = {
        "questionId": Number(selectQuestion),
        "responses": photoObjs
      }
      const record = await addQuestionToSession({ 
        path: 'sessionQuestion', 
        data, 
        token: authenticatedUser.value, 
        sessionId: session.sessionId 
      });

      if (Object.prototype.hasOwnProperty.call(record, 'questionId')) {
        let sessionData = await addPA({
          path: `userQuestion/${session.sessionCode}`,
          data: {
            sessionQstId: record.sessionQstId,
            questionId: record.questionId,
            sessionId: session.sessionId,
            sessionCode: session.sessionCode,
          },
          token: authenticatedUser.value
        })

        if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionUsers')) {
          const questionExists = session.questions.some((question) => question.sessionQstId === record.sessionQstId)
          if (!questionExists) {
            session.questions.push(record)
          }
          session.sessionUsers = session.sessionUsers.map((userDetails) => ({
            ...userDetails,
            userAnswers: updateUserQuestions({
              userId: userDetails.userId,
              sessionId: session.sessionId,
              sessionCode: session.sessionCode,
              currentQuestions: userDetails.userAnswers,
              newQuestion: {
                ...record,
                responses: []
              }
            })
          }))
          setSession(session)
          socket.emit('Canvas', { 
            session: params.id, 
            type: 'ADD_QUESTION', 
            question: record, 
            userId: authenticatedUser.userId 
          })
          setShowPhoto(false)
          setPhotosObjs([])
          setQuestion('')
        } else {
          setAlert({
            message: sessionData.message,
            msgType: 'warning'
          })
        }
      } else {
        setAlert({
          message: record.message,
          msgType: 'warning'
        })
      }
    }
  }

  const handleChangeRemoveQuestion = (question, e) => {
    e.preventDefault()
    setQuestionToDelete(question)
    setShowDeleteModal(true)
  }

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return

    setIsDeleting(true)
    try {
      const record = await deleteByIdPA({
        path: 'sessionQuestion',
        id: questionToDelete.sessionQstId,
        token: authenticatedUser.value
      })

      if (record.deleted) {
        if (Object.prototype.hasOwnProperty.call(record, 'id')) {
          session.questions = session.questions.filter((question) => question.sessionQstId !== record.id)
          session.sessionUsers = session.sessionUsers.map((userDetails) => ({
            ...userDetails,
            userAnswers: userDetails.userAnswers.filter((answer) => answer.sessionQstId !== questionToDelete.sessionQstId)
          }))

          setSession(session)
          socket.emit('Canvas', { 
            session: params.id, 
            type: 'REMOVE_QUESTION', 
            sessionQstId: record.id, 
            userId: authenticatedUser.userId 
          })
        }
        setAlert({
          message: 'Pregunta eliminada exitosamente',
          msgType: 'success'
        })
      } else {
        setAlert({
          message: record.message,
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al eliminar la pregunta',
        msgType: 'warning'
      })
    } finally {
      setIsDeleting(false)
      cancelDeleteQuestion()
    }
  }

  const cancelDeleteQuestion = () => {
    setShowDeleteModal(false)
    setQuestionToDelete(null)
    setIsDeleting(false)
  }

  const closeSession = async ({ sessionId }) => {
    const record = await patchByIdPA({
      path: 'sessions',
      id: sessionId,
      data: {
        active: false,
        reportReady: true
      },
      token: authenticatedUser.value
    })
    
    if (Object.prototype.hasOwnProperty.call(record, 'sessionId')) {
      setSessionOpen(false)
      socket.emit('Canvas', { session: params.id, type: 'CLOSE_SESSION' })
      navigate('/sessions')
    }
    return sessionId
  }

  const handleLinkCopy = () => {
    setLinkCopied(true)
    setTimeout(() => {
      setLinkCopied(false)
    }, 2000)
  }

  const handleViewQuestionDetail = (question) => {
    setSelectedQuestion(question)
    setShowQuestionDetail(true)
  }

  const handleImageClick = (image, userName, phrase, allResponses = [], currentIndex = 0) => {
    console.log('Image clicked:', { image, userName, phrase, allResponses, currentIndex })
    setSelectedImage({
      src: image,
      userName,
      phrase,
      questionText: selectedQuestion?.question,
      allImages: allResponses,
      currentIndex: currentIndex
    })
    setShowImageViewer(true)
    console.log('Opening image viewer modal')
  }

  // Funciones de navegación de imágenes
  const handleNextImage = () => {
    if (selectedImage && selectedImage.allImages && selectedImage.currentIndex < selectedImage.allImages.length - 1) {
      const nextIndex = selectedImage.currentIndex + 1
      const nextResponse = selectedImage.allImages[nextIndex]
      setSelectedImage({
        ...selectedImage,
        src: nextResponse.image,
        phrase: nextResponse.phrase,
        currentIndex: nextIndex
      })
    }
  }

  const handlePrevImage = () => {
    if (selectedImage && selectedImage.allImages && selectedImage.currentIndex > 0) {
      const prevIndex = selectedImage.currentIndex - 1
      const prevResponse = selectedImage.allImages[prevIndex]
      setSelectedImage({
        ...selectedImage,
        src: prevResponse.image,
        phrase: prevResponse.phrase,
        currentIndex: prevIndex
      })
    }
  }

  // Funciones para manejar vistas

  const handleViewUserDetail = (user) => {
    setSelectedUser(user)
    setShowUserDetail(true)
  }

  const closeUserDetail = () => {
    setShowUserDetail(false)
    setSelectedUser(null)
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  const getQuestionStats = (question) => {
    if (!session.sessionUsers) return { answered: 0, total: 0 }
    
    const guests = session.sessionUsers.filter(user => user.userRole === 'INVITADO')
    const answered = guests.filter(user => {
      const userAnswer = user.userAnswers?.find(answer => answer.sessionQstId === question.sessionQstId)
      return userAnswer && userAnswer.responses && userAnswer.responses.length > 0
    }).length
    
    return { answered, total: guests.length }
  }

  // Manejo de eventos de socket (copiado de Canvas.js)
  const handleEvent = (event) => {
    if (params.id === event.session && session) {
      if (event.userId !== authenticatedUser.userId) {
        switch (event.type) {
          case 'START_SESSION':
            const userInSession = session.sessionUsers.some((user) => user.userId === event.user.userId)
            if (!userInSession && event.user.userRole === 'INVITADO') {
              setAlert({
                message: `${event.user.userFullName} se unió a la sesión`,
                msgType: 'success'
              })
              setSession(prevSession => ({
                ...prevSession,
                sessionUsers: sortUsersByName([...prevSession.sessionUsers, event.user])
              }))
            }
            break

          case 'ADD_QUESTION':
            const question = event.question
            if (question) {
              setAlert({
                message: `Nueva pregunta agregada: "${question.question}"`,
                msgType: 'success'
              })
              
              const questionExists = session.questions.some((item) => item.sessionQstId === question.sessionQstId)
              if (!questionExists) {
                setSession(prevSession => ({
                  ...prevSession,
                  questions: [...prevSession.questions, question]
                }))
              }
            }
            break

          case 'REMOVE_QUESTION':
            const questionToRemove = event.question
            if (questionToRemove) {
              setAlert({
                message: `Pregunta eliminada: "${questionToRemove.question}"`,
                msgType: 'warning'
              })
              
              setSession(prevSession => ({
                ...prevSession,
                questions: prevSession.questions.filter((item) => item.sessionQstId !== questionToRemove.sessionQstId)
              }))
            }
            break

          case 'UPDATE_ANSWER':
            const userOnSession = session.sessionUsers.some((user) => user.userId === event.user.userId)
            if (userOnSession) {
              setSession(prevSession => {
                const updatedUsers = prevSession.sessionUsers.map((user) => ({
                  ...user,
                  userAnswers: user.userId === event.userId ? event.user.userAnswers : user.userAnswers
                }))
                return {
                  ...prevSession,
                  sessionUsers: sortUsersByName(updatedUsers)
                }
              })
            }
            break

          case 'USER_LEFT':
            const isInSession = session.sessionUsers.some((user) => user.userId === event.user.userId)
            if (isInSession) {
              setAlert({
                message: `El usuario: ${event.user.userFullName} ha abandonado la sesión`,
                msgType: 'warning'
              })
              setSession(prevSession => ({
                ...prevSession,
                sessionUsers: prevSession.sessionUsers.filter((user) => user.userId !== event.user.userId)
              }))
            }
            break

          default:
            break
        }
      }
    }
  }

  // Configurar listeners de socket
  useEffect(() => {
    const handleUsuarioEvent = (event) => {
      handleEvent(event)
    }

    const handleCanvasEvent = (event) => {
      handleEvent(event)
    }

    socket.on('Usuario', handleUsuarioEvent)
    socket.on('Canvas', handleCanvasEvent)

    // Cleanup function
    return () => {
      socket.off('Usuario', handleUsuarioEvent)
      socket.off('Canvas', handleCanvasEvent)
    }
  }, [session, authenticatedUser.userId, params.id])

  // Pantalla de carga
  if (isLoading && !loadingTimeout) {
    return (
      <div className="host-canvas-container">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-icon">
              <FaSpinner className="spinning" />
            </div>
            <h2 className="loading-title">Conectando con la sesión</h2>
            <p className="loading-message">{loadingMessage}</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de error después del timeout
  if (loadingTimeout) {
    return (
      <div className="host-canvas-container">
        <div className="error-state-friendly">
          <div className="error-content">
            <div className="error-icon">
              <FaWifi className="wifi-icon" />
            </div>
            <h2 className="error-title">Problema de conexión</h2>
            <p className="error-message">
              No pudimos conectar con la sesión. Esto puede deberse a problemas de red o del servidor.
            </p>
            <div className="error-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setIsLoading(true)
                  setLoadingTimeout(false)
                  fetchData()
                }}
                className="retry-btn"
              >
                <FaSpinner className="me-2" />
                Intentar de nuevo
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => window.location.href = '/'}
                className="home-btn"
              >
                Volver al inicio
              </Button>
            </div>
            <div className="error-help">
              <FaExclamationCircle className="help-icon" />
              <p>Si el problema persiste, contacta al administrador del sistema</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de error de sesión inválida
  if (!validSession) {
    return (
      <div className="host-canvas-container">
        <div className="error-state-friendly">
          <div className="error-content">
            <div className="error-icon">
              <FaExclamationCircle className="error-icon-main" />
            </div>
            <h2 className="error-title">Sesión no encontrada</h2>
            <p className="error-message">
              {message || 'La sesión que buscas no existe o no tienes permisos para acceder a ella.'}
            </p>
            <div className="error-actions">
              <Button
                variant="primary"
                onClick={() => window.location.href = '/'}
                className="home-btn"
              >
                Volver al inicio
              </Button>
            </div>
            <div className="error-help">
              <FaPhone className="help-icon" />
              <p>Si crees que esto es un error, contacta al administrador</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="host-canvas-container">
      {/* Header del Anfitrión */}
      <div className="host-header">
        <div className="header-left">
          <Button
            variant="outline-secondary"
            onClick={() => window.location.href = '/'}
            className="back-btn"
            title="Volver al dashboard"
          >
            <FaArrowLeft />
            <span>Volver</span>
          </Button>
        </div>
        
        <div className="header-center">
          <div className="session-title-content">
            <h1 className="session-title">{session.sessionName}</h1>
            {session.description && (
              <p className="session-description">{session.description}</p>
            )}
          </div>
        </div>
        
        <div className="header-right">
          <div className="action-buttons">
            <CopyToClipboard 
              text={`${window.location.origin}/sesion/${params.id}`}
              onCopy={handleLinkCopy}
            >
              <Button
                variant="outline-primary"
                className="copy-link-btn"
              >
                <FaCopy />
                <span>{linkCopied ? '¡Copiado!' : 'Copiar Enlace'}</span>
              </Button>
            </CopyToClipboard>
            <Button
              variant="danger"
              onClick={async () => await closeSession({ sessionId: session.sessionId })}
              className="close-session-btn"
            >
              <FaSignOutAlt />
              <span>Cerrar Sesión</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Métricas del Dashboard */}
      <Row className="metrics-row">
        <Col md={3} sm={6} className="mb-3">
          <Card className="metric-card">
            <Card.Body className="text-center">
              <FaUsers className="metric-icon" />
              <div className="metric-value">{connectedGuests}</div>
              <div className="metric-label">Invitados Conectados</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="metric-card">
            <Card.Body className="text-center">
              <FaQuestionCircle className="metric-icon" />
              <div className="metric-value">{totalQuestions}</div>
              <div className="metric-label">Preguntas Activas</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="metric-card">
            <Card.Body className="text-center">
              <FaChartLine className="metric-icon" />
              <div className="metric-value">{completionRate}%</div>
              <div className="metric-label">Progreso General</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="metric-card">
            <Card.Body className="text-center">
              <FaClock className="metric-icon" />
              <div className="metric-value">{sessionDuration}</div>
              <div className="metric-label">Tiempo de Sesión</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Preguntas */}
      <Card className="questions-card">
        <Card.Header className="questions-header">
          <div className="header-content">
            <h3>Preguntas de la Sesión</h3>
            <Button
              variant="outline-warning"
              onClick={handleChangeAddQuestions}
              className="add-question-btn"
            >
              <FaPlus />
              <span>Agregar Pregunta</span>
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {session.questions && session.questions.length > 0 ? (
            <div className="questions-list">
              {session.questions.map((question, index) => {
                const stats = getQuestionStats(question)
                const progressPercentage = stats.total > 0 ? Math.round((stats.answered / stats.total) * 100) : 0
                
                const isCompleted = stats.answered === stats.total && stats.total > 0
                
                return (
                  <div key={question.sessionQstId} className={`question-item ${isCompleted ? 'completed' : ''}`}>
                    <div className="question-number">#{index + 1}</div>
                    <div className="question-content">
                      <div className="question-text">{question.question}</div>
                      <div className="question-stats">
                        <div className="progress-info">
                          <span className="progress-text">
                            {stats.answered}/{stats.total} respondieron
                          </span>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${progressPercentage}%` }}
                            ></div>
                          </div>
                          <span className="progress-percentage">{progressPercentage}%</span>
                        </div>
                      </div>
                      <div className="question-actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewQuestionDetail(question)}
                          className="view-btn"
                        >
                          <FaEye aria-hidden />
                          <span>Ver Respuestas</span>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => handleChangeRemoveQuestion(question, e)}
                          className="delete-btn"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="empty-questions">
              <FaQuestionCircle className="empty-icon" />
              <h4>No hay preguntas configuradas</h4>
              <p>Agrega preguntas para que los invitados puedan participar</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Lista de Invitados */}
      <Card className="guests-card">
        <Card.Header className="guests-header">
          <div className="guests-title">
            <h3>Invitados Conectados</h3>
            <p>Visualiza y gestiona las respuestas de los invitados</p>
          </div>
          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="clear-search-btn"
                  type="button"
                >
                  <FaTimesCircle />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="search-results-info">
                {filteredUsers.length} de {session.sessionUsers?.filter(user => user.userRole === 'INVITADO').length || 0} invitados
              </div>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {filteredUsers && filteredUsers.length > 0 ? (
            // Vista de Listado (fija)
            <div className="guests-list-view">
              <div className="guests-table">
                <div className="table-header">
                  <div className="header-cell">Invitado</div>
                  <div className="header-cell">Respuestas</div>
                  <div className="header-cell">Progreso</div>
                  <div className="header-cell">Acciones</div>
                </div>
                {filteredUsers.map((user) => {
                    const userAnswers = user.userAnswers || []
                    const answeredQuestions = userAnswers.filter(answer => 
                      answer.responses && answer.responses.length > 0
                    ).length
                    const totalQuestions = session.questions.length
                    const progressPercentage = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
                    
                    return (
                      <div key={user.userId} className="table-row">
                        <div className="table-cell user-cell">
                          <div className="user-avatar-small">
                            {user.userFullName.charAt(0).toUpperCase()}
                          </div>
                          <div className="user-info-small">
                            <div className="user-name-small">{user.userFullName}</div>
                            <div className="user-email-small">{user.userEmail}</div>
                          </div>
                        </div>
                        <div className="table-cell">
                          <span className="answers-count">
                            {answeredQuestions}/{totalQuestions}
                          </span>
                        </div>
                        <div className="table-cell">
                          <div className="progress-circle-small">
                            <div 
                              className="progress-fill-circle"
                              style={{
                                background: `conic-gradient(#ff6b35 0deg, #f7931e ${progressPercentage * 3.6}deg, #e2e8f0 ${progressPercentage * 3.6}deg)`
                              }}
                            >
                              <div className="progress-percentage-small">{progressPercentage}%</div>
                            </div>
                          </div>
                        </div>
                        <div className="table-cell">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewUserDetail(user)}
                            className="view-user-btn"
                          >
                            <FaEye aria-hidden />
                            <span>Ver Detalles</span>
                          </Button>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          ) : searchTerm ? (
            <div className="empty-guests">
              <FaSearch className="empty-icon" />
              <h4>No se encontraron resultados</h4>
              <p>No hay invitados que coincidan con "{searchTerm}"</p>
              <Button
                variant="outline-primary"
                onClick={clearSearch}
                className="clear-search-btn-large"
              >
                Limpiar búsqueda
              </Button>
            </div>
          ) : (
            <div className="empty-guests">
              <FaUsers className="empty-icon" />
              <h4>No hay invitados conectados</h4>
              <p>Los invitados aparecerán aquí cuando se unan a la sesión</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modales */}
      <AddQuestionToCanvas
        showModalQuestion={show}
        handleClose={handleClose}
        onClickNext={onClickSelectPics}
        onblurCreateQuestion={handleChangeQuestion}
        newQuestion={newQuestion}
        setNewQuestion={handleAddNewQuestionToggle}
        handleOnSelectQuestion={handleOnSelectQuestion}
      />

      <AddPhotoToCanvas
        showModalPic={showPhoto}
        handleClose={() => setShowPhoto(false)}
        setPhotosObjs={setPhotosObjs}
        saveQuestionToSession={saveQuestionToSession}
      />

      <VideoTutorial
        showModalVideo={showVideo}
        handleClose={() => setShowVideo(false)}
      />

      {/* Modal de Confirmación de Eliminación */}
      <Modal 
        show={showDeleteModal} 
        onHide={cancelDeleteQuestion}
        centered
        backdrop="static"
        keyboard={false}
        className="delete-confirm-modal"
      >
        <Modal.Header className="delete-modal-header">
          <div className="modal-icon-container">
            <FaExclamationTriangle className="modal-warning-icon" />
          </div>
          <div className="modal-title-container">
            <Modal.Title className="modal-title">Confirmar Eliminación</Modal.Title>
            <p className="modal-subtitle">Esta acción no se puede deshacer</p>
          </div>
          <Button 
            variant="link" 
            onClick={cancelDeleteQuestion}
            className="modal-close-btn"
            disabled={isDeleting}
          >
            <FaTimes />
          </Button>
        </Modal.Header>
        
        <Modal.Body className="delete-modal-body">
          <div className="question-preview">
            <h6 className="preview-label">Pregunta a eliminar:</h6>
            <div className="question-content">
              <p className="question-text">
                <strong>ID:</strong> #{questionToDelete?.sessionQstId}
              </p>
              <p className="question-text">
                <strong>Pregunta:</strong> {questionToDelete?.question}
              </p>
            </div>
          </div>
          
          <div className="warning-message">
            <FaExclamationTriangle className="warning-icon" />
            <p>¿Estás seguro de que quieres eliminar esta pregunta? Esta acción es permanente y no se puede deshacer.</p>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="delete-modal-footer">
          <Button 
            variant="outline-secondary" 
            onClick={cancelDeleteQuestion}
            className="cancel-delete-btn"
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger"
            onClick={confirmDeleteQuestion}
            className="confirm-delete-btn"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Eliminando...
              </>
            ) : (
              'Eliminar Pregunta'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Detalle de Pregunta - VERSIÓN MEJORADA */}
      <Modal 
        show={showQuestionDetail} 
        onHide={() => setShowQuestionDetail(false)}
        size="xl"
        centered
        className="question-detail-modal-advanced"
        backdrop="static"
      >
        <div className="modal-glass-wrapper">
          {/* Fondo animado con partículas */}
          <div className="modal-animated-bg">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
          </div>

          {/* Botón cerrar mejorado */}
          <button 
            className="modal-close-enhanced"
            onClick={() => setShowQuestionDetail(false)}
            aria-label="Cerrar modal"
          >
            <FaTimes />
          </button>

          <Modal.Body className="p-0">
            {selectedQuestion && (
              <div className="question-detail-container">
                {/* Header con estadísticas */}
                <div className="question-detail-header">
                  <div className="question-header-content">
                    <div className="question-icon-wrapper">
                      <FaQuestionCircle className="question-icon-large" />
                      <div className="icon-glow"></div>
                    </div>
                    <div className="question-title-section">
                      <h2 className="question-title-advanced">{selectedQuestion.question}</h2>
                      <div className="question-meta">
                        <span className="meta-item">
                          <FaUsers className="meta-icon" />
                          {session.sessionUsers.filter(u => {
                            const ans = u.userAnswers?.find(a => a.sessionQstId === selectedQuestion.sessionQstId);
                            return ans && ans.responses && ans.responses.length > 0;
                          }).length} de {session.sessionUsers.length} respondieron
                        </span>
                        <span className="meta-divider">•</span>
                        <span className="meta-item">
                          <FaImages className="meta-icon" />
                          {session.sessionUsers.reduce((total, u) => {
                            const ans = u.userAnswers?.find(a => a.sessionQstId === selectedQuestion.sessionQstId);
                            return total + (ans?.responses?.length || 0);
                          }, 0)} respuestas totales
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Barra de progreso mejorada */}
                  <div className="progress-section-advanced">
                    <div className="progress-labels">
                      <span className="progress-label-text">Progreso General</span>
                      <span className="progress-percentage-advanced">
                        {Math.round((session.sessionUsers.filter(u => {
                          const ans = u.userAnswers?.find(a => a.sessionQstId === selectedQuestion.sessionQstId);
                          return ans && ans.responses && ans.responses.length > 0;
                        }).length / session.sessionUsers.length) * 100) || 0}%
                      </span>
                    </div>
                    <div className="progress-bar-advanced">
                      <div 
                        className="progress-fill-advanced"
                        style={{
                          width: `${Math.round((session.sessionUsers.filter(u => {
                            const ans = u.userAnswers?.find(a => a.sessionQstId === selectedQuestion.sessionQstId);
                            return ans && ans.responses && ans.responses.length > 0;
                          }).length / session.sessionUsers.length) * 100) || 0}%`
                        }}
                      >
                        <div className="progress-shine"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grid de respuestas con virtualización */}
                <div className="responses-grid-container">
                  <div className="responses-grid-advanced">
                    {session.sessionUsers.map((user, userIndex) => {
                      const userAnswer = user.userAnswers?.find(answer => answer.sessionQstId === selectedQuestion.sessionQstId)
                      const hasResponses = userAnswer && userAnswer.responses && userAnswer.responses.length > 0
                      
                      return (
                        <div 
                          key={user.userId} 
                          className={`response-card-advanced ${hasResponses ? 'has-responses' : 'no-responses'}`}
                          style={{ animationDelay: `${userIndex * 0.05}s` }}
                        >
                          {/* Header de usuario mejorado */}
                          <div className="response-card-header">
                            <div className="user-avatar-advanced">
                              <div className="avatar-inner">
                                {user.userFullName.charAt(0).toUpperCase()}
                              </div>
                              <div className="avatar-ring"></div>
                              <div className="avatar-pulse"></div>
                            </div>
                            <div className="user-info-advanced">
                              <h4 className="user-name-advanced">{user.userFullName}</h4>
                              <span className={`status-badge-advanced ${hasResponses ? 'status-completed' : 'status-pending'}`}>
                                {hasResponses ? (
                                  <>
                                    <FaCheckCircle className="status-icon" />
                                    <span>{userAnswer.responses.length} {userAnswer.responses.length === 1 ? 'respuesta' : 'respuestas'}</span>
                                  </>
                                ) : (
                                  <>
                                    <FaHourglassHalf className="status-icon" />
                                    <span>Pendiente</span>
                                  </>
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Galería de imágenes mejorada */}
                          {hasResponses ? (
                            <div className="images-gallery-advanced">
                              {userAnswer.responses.map((response, responseIndex) => (
                                <div 
                                  key={responseIndex} 
                                  className="image-card-advanced"
                                  onClick={() => handleImageClick(response.image, user.userFullName, response.phrase, userAnswer.responses, responseIndex)}
                                >
                                  <div className="image-wrapper-advanced">
                                    <img 
                                      src={response.image} 
                                      alt={`Respuesta ${responseIndex + 1}`}
                                      className="image-advanced"
                                      loading="lazy"
                                    />
                                    <div className="image-overlay-advanced">
                                      <div className="overlay-content-advanced">
                                        <FaExpandAlt className="expand-icon-advanced" />
                                        <span className="expand-text">Ampliar</span>
                                      </div>
                                    </div>
                                    <div className="image-number-badge">#{responseIndex + 1}</div>
                                  </div>
                                  {response.phrase && (
                                    <div className="phrase-container-advanced">
                                      <div className="phrase-quote-mark">"</div>
                                      <p className="phrase-text-advanced">{response.phrase}</p>
                                      <div className="phrase-quote-mark quote-end">"</div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state-advanced">
                              <div className="empty-icon-container">
                                <FaInbox className="empty-icon-advanced" />
                                <div className="empty-icon-circle"></div>
                              </div>
                              <p className="empty-text-advanced">Aún no ha respondido</p>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </div>
      </Modal>

      {/* Modal de Imagen Ampliada - Versión Mejorada */}
      <Modal 
        show={showImageViewer} 
        onHide={() => setShowImageViewer(false)}
        size="xl"
        centered
        className="image-viewer-modal-enhanced"
        backdrop="static"
      >
        <Modal.Body className="p-0">
          {selectedImage && (
            <div className="enhanced-image-viewer">
              {/* Botón de cerrar mejorado */}
              <button 
                className="enhanced-close-btn"
                onClick={() => setShowImageViewer(false)}
                aria-label="Cerrar"
              >
                <FaTimes />
              </button>

              {/* Contenedor principal con grid layout */}
              <div className="viewer-grid-container">
                {/* Columna izquierda: Imagen principal */}
                <div className="viewer-image-section">
                  {/* Indicador de posición superior */}
                  {selectedImage.allImages && selectedImage.allImages.length > 1 && (
                    <div className="position-badge">
                      <span className="current-number">{selectedImage.currentIndex + 1}</span>
                      <span className="separator">/</span>
                      <span className="total-number">{selectedImage.allImages.length}</span>
                    </div>
                  )}

                  {/* Imagen con efecto de zoom */}
                  <div className="image-container-enhanced">
                    <img 
                      src={selectedImage.src} 
                      alt="Imagen ampliada" 
                      className="enhanced-image"
                      key={selectedImage.currentIndex}
                    />
                    
                    {/* Overlay gradient sutil */}
                    <div className="image-gradient-overlay"></div>
                  </div>

                  {/* Controles de navegación integrados */}
                  {selectedImage.allImages && selectedImage.allImages.length > 1 && (
                    <div className="navigation-controls">
                      <button 
                        className={`nav-control prev ${selectedImage.currentIndex === 0 ? 'disabled' : ''}`}
                        onClick={handlePrevImage}
                        disabled={selectedImage.currentIndex === 0}
                        aria-label="Anterior"
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {/* Indicadores de puntos */}
                      <div className="dots-indicator">
                        {selectedImage.allImages.map((_, index) => (
                          <div 
                            key={index}
                            className={`dot ${index === selectedImage.currentIndex ? 'active' : ''}`}
                          />
                        ))}
                      </div>

                      <button 
                        className={`nav-control next ${selectedImage.currentIndex === selectedImage.allImages.length - 1 ? 'disabled' : ''}`}
                        onClick={handleNextImage}
                        disabled={selectedImage.currentIndex === selectedImage.allImages.length - 1}
                        aria-label="Siguiente"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
                </div>

                {/* Columna derecha: Información y miniaturas */}
                <div className="viewer-info-section">
                  {/* Header con avatar del usuario */}
                  <div className="user-header-card">
                    <div className="user-avatar-circle">
                      {selectedImage.userName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-info-text">
                      <h4 className="user-name-title">{selectedImage.userName}</h4>
                      <span className="user-role-badge">Invitado</span>
                    </div>
                  </div>

                  {/* Frase destacada */}
                  {selectedImage.phrase && (
                    <div className="phrase-card">
                      <div className="quote-icon-left">
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="currentColor">
                          <path d="M0 10.5C0 5 3 0 9 0v4C6 5 5 7 5 10h4v10H0v-9.5zM15 10.5C15 5 18 0 24 0v4c-3 1-4 3-4 6h4v10h-9v-9.5z"/>
                        </svg>
                      </div>
                      <p className="phrase-text">{selectedImage.phrase}</p>
                      <div className="quote-icon-right">
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="currentColor">
                          <path d="M24 9.5C24 15 21 20 15 20v-4c3-1 4-3 4-6h-4V0h9v9.5zM9 9.5C9 15 6 20 0 20v-4c3-1 4-3 4-6H0V0h9v9.5z"/>
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Miniaturas de navegación */}
                  {selectedImage.allImages && selectedImage.allImages.length > 1 && (
                    <div className="thumbnails-section">
                      <h5 className="thumbnails-title">
                        <FaImages className="me-2" />
                        Todas las fotos ({selectedImage.allImages.length})
                      </h5>
                      <div className="thumbnails-grid">
                        {selectedImage.allImages.map((response, index) => (
                          <div 
                            key={index}
                            className={`thumbnail-item ${index === selectedImage.currentIndex ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedImage({
                                ...selectedImage,
                                src: response.image,
                                phrase: response.phrase,
                                currentIndex: index
                              })
                            }}
                          >
                            <img 
                              src={response.image} 
                              alt={`Miniatura ${index + 1}`}
                              className="thumbnail-image"
                            />
                            {index === selectedImage.currentIndex && (
                              <div className="thumbnail-active-badge">
                                <FaCheckCircle />
                              </div>
                            )}
                            <div className="thumbnail-overlay">
                              <span className="thumbnail-number">#{index + 1}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metadata adicional */}
                  <div className="metadata-card">
                    <div className="metadata-item">
                      <FaQuestionCircle className="metadata-icon" />
                      <div className="metadata-content">
                        <span className="metadata-label">Pregunta</span>
                        <span className="metadata-value">{selectedImage.questionText || 'Sin pregunta'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de Detalles del Usuario - Versión Impresionante */}
      <Modal 
        show={showUserDetail} 
        onHide={closeUserDetail}
        size="lg"
        centered
        className="user-detail-modal-wow"
      >
        <div className="modal-glass-container">
          {/* Patrón de fondo animado */}
          <div className="modal-bg-pattern">
            <div className="pattern-circle pattern-1"></div>
            <div className="pattern-circle pattern-2"></div>
            <div className="pattern-circle pattern-3"></div>
          </div>

          {/* Botón cerrar flotante */}
          <button className="modal-close-floating" onClick={closeUserDetail}>
            <FaTimes />
          </button>

          <Modal.Body className="p-0">
            {selectedUser && (
              <div className="user-detail-wow-content">
                {/* Header con avatar grande y efecto */}
                <div className="user-header-wow">
                  <div className="avatar-glow-wrapper">
                    <div className="avatar-glow"></div>
                    <div className="user-avatar-wow">
                      {selectedUser.userFullName.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <h3 className="user-name-wow">{selectedUser.userFullName}</h3>
                  <p className="user-email-wow">{selectedUser.userEmail}</p>
                </div>

                {/* Estadísticas con círculos animados */}
                <div className="stats-wow-container">
                  <div className="stat-card-wow">
                    <div className="stat-circle-wrapper">
                      <svg className="stat-circle-bg" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255, 107, 53, 0.1)" strokeWidth="6"/>
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="45" 
                          fill="none" 
                          stroke="url(#gradient1)" 
                          strokeWidth="6"
                          strokeDasharray="283"
                          strokeDashoffset="0"
                          strokeLinecap="round"
                          className="stat-circle-progress"
                        />
                        <defs>
                          <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ff6b35" />
                            <stop offset="100%" stopColor="#f7931e" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="stat-circle-content">
                        <FaQuestionCircle className="stat-circle-icon" />
                        <span className="stat-circle-value">{session.questions.length}</span>
                      </div>
                    </div>
                    <span className="stat-circle-label">Total</span>
                  </div>

                  <div className="stat-card-wow">
                    <div className="stat-circle-wrapper">
                      <svg className="stat-circle-bg" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(34, 197, 94, 0.1)" strokeWidth="6"/>
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="45" 
                          fill="none" 
                          stroke="url(#gradient2)" 
                          strokeWidth="6"
                          strokeDasharray="283"
                          strokeDashoffset="0"
                          strokeLinecap="round"
                          className="stat-circle-progress"
                        />
                        <defs>
                          <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#22c55e" />
                            <stop offset="100%" stopColor="#16a34a" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="stat-circle-content">
                        <FaCheckCircle className="stat-circle-icon" />
                        <span className="stat-circle-value">
                          {selectedUser.userAnswers?.filter(answer => 
                            answer.responses && answer.responses.length > 0
                          ).length || 0}
                        </span>
                      </div>
                    </div>
                    <span className="stat-circle-label">Respondidas</span>
                  </div>

                  <div className="stat-card-wow">
                    <div className="stat-circle-wrapper">
                      <svg className="stat-circle-bg" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(59, 130, 246, 0.1)" strokeWidth="6"/>
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="45" 
                          fill="none" 
                          stroke="url(#gradient3)" 
                          strokeWidth="6"
                          strokeDasharray="283"
                          strokeDashoffset={283 - (283 * (session.questions.length > 0 ? 
                            ((selectedUser.userAnswers?.filter(answer => 
                              answer.responses && answer.responses.length > 0
                            ).length || 0) / session.questions.length) : 0))}
                          strokeLinecap="round"
                          className="stat-circle-progress stat-progress-animated"
                        />
                        <defs>
                          <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#2563eb" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="stat-circle-content">
                        <FaChartLine className="stat-circle-icon" />
                        <span className="stat-circle-value">
                          {session.questions.length > 0 ? 
                            Math.round(((selectedUser.userAnswers?.filter(answer => 
                              answer.responses && answer.responses.length > 0
                            ).length || 0) / session.questions.length) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                    <span className="stat-circle-label">Progreso</span>
                  </div>
                </div>

                {/* Separador con título */}
                <div className="section-divider-wow">
                  <div className="divider-line"></div>
                  <span className="divider-text">
                    <FaImages className="me-2" />
                    Respuestas Detalladas
                  </span>
                  <div className="divider-line"></div>
                </div>

                {/* Lista de preguntas mejorada */}
                <div className="questions-container-wow">
                  {session.questions.map((question, index) => {
                    const userAnswer = selectedUser.userAnswers?.find(answer => 
                      answer.sessionQstId === question.sessionQstId
                    )
                    const hasResponses = userAnswer && userAnswer.responses && userAnswer.responses.length > 0
                    
                    return (
                      <div 
                        key={question.sessionQstId} 
                        className={`question-card-wow ${hasResponses ? 'has-response' : 'no-response'}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="question-header-wow">
                          <div className="question-number-wow">
                            <span className="number-text">{index + 1}</span>
                            <div className="number-glow"></div>
                          </div>
                          <h5 className="question-title-wow">{question.question}</h5>
                          {hasResponses ? (
                            <div className="status-badge-wow success">
                              <FaCheckCircle />
                              <span>{userAnswer.responses.length}</span>
                            </div>
                          ) : (
                            <div className="status-badge-wow pending">
                              <FaHourglassHalf />
                              <span>Pendiente</span>
                            </div>
                          )}
                        </div>
                        
                        {hasResponses && (
                          <div className="images-gallery-wow">
                            {userAnswer.responses.map((response, idx) => (
                              <div 
                                key={idx} 
                                className="image-item-wow"
                                style={{ animationDelay: `${(index * 0.1) + (idx * 0.05)}s` }}
                              >
                                <div 
                                  className="image-frame-wow"
                                  onClick={() => handleImageClick(response.image, selectedUser.userFullName, response.phrase, userAnswer.responses, idx)}
                                >
                                  <div className="image-shine"></div>
                                  <img 
                                    src={response.image} 
                                    alt={`Respuesta ${idx + 1}`}
                                    className="image-wow"
                                  />
                                  <div className="image-hover-overlay">
                                    <div className="overlay-content">
                                      <FaExpandAlt className="expand-icon-wow" />
                                      <span className="expand-text">Ver imagen</span>
                                    </div>
                                  </div>
                                  <div className="image-number-badge-wow">{idx + 1}</div>
                                </div>
                                {response.phrase && (
                                  <div className="phrase-bubble-wow">
                                    <div className="phrase-quote-left">"</div>
                                    <p className="phrase-text-wow">{response.phrase}</p>
                                    <div className="phrase-quote-right">"</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {!hasResponses && (
                          <div className="empty-state-wow">
                            <div className="empty-icon-wrapper">
                              <FaInbox className="empty-icon-wow" />
                              <div className="empty-icon-pulse"></div>
                            </div>
                            <p className="empty-text-wow">Aún no hay respuestas para esta pregunta</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </Modal.Body>
        </div>
      </Modal>
    </div>
  )
}
